/*
 * Copyright (c) 2025, FusionAuth, All Rights Reserved
 */

import CodeMirror from "codemirror";
// the various modes we use
import "codemirror/mode/css/css.js";
import "codemirror/mode/properties/properties";
import "codemirror/mode/shell/shell.js";
import "codemirror/mode/xml/xml.js";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/addon/lint/lint.js";
import "codemirror/addon/lint/javascript-lint.js";
import "codemirror/addon/lint/json-lint.js";
import "../tools/codemirror-freemarker-5.17.0";
import {JSHINT} from "jshint/dist/jshint.js";
import {jsonlint} from "../tools/jsonlint-0.1.0";

// global for compatibility
declare global {
  var Prime;

  interface FusionAuthUI {
    TextEditor: object;
  }

  interface FusionAuth {
    UI: FusionAuthUI;
  }

  interface Window {
    FusionAuth: FusionAuth;
    JSHINT: any;
    jsonlint: any;
  }
}

window.FusionAuth = window.FusionAuth || {};
const FusionAuth = window.FusionAuth;
FusionAuth.UI = FusionAuth.UI || {};
// CodeMirror expects these to be available as globals
window.JSHINT = JSHINT;
window.jsonlint = jsonlint;

/**
 * TextEditor class for managing CodeMirror instances.
 */
class TextEditor {
  private initialized: boolean;
  private options: object;
  private keys: any[];
  private element: any;
  private editor: any;
  private editorElement: any;

  /**
   * Constructs a new TextEditor instance for the given element.
   *
   * @param {PrimeElement|Element} element The element to create the TextEditor for.
   */
  constructor(element) {
    Prime.Utils.bindAll(this);

    // CodeMirror 5.17.0 seems to struggle with the new admin layout when rendered
    // with the parent element in hidden state. The gutter shows up off, to the left.
    // A workaround is to refresh the editor once the element is visible.
    const parentSlideOpen = element.queryUp(".slide-open");
    if (parentSlideOpen !== null) {
      // now when parentSlideOpen has the open class added to it, refresh our editor
      const setupEditor = (mutationsList) => {
        if (mutationsList[0].target.classList.contains("open")) {
          this.refresh();
        }
      };

      const observer = new MutationObserver(setupEditor);
      const config = {attributes: true, attributeFilter: ['class']};
      observer.observe(parentSlideOpen.domElement, config);
    }

    this.initialized = false;
    this.options = {
      autofocus: false,
      lineNumbers: true,
      lineWrapping: true,
      mode: 'freemarker'
    };

    this.keys = [];
    this.element = Prime.Document.Element.wrap(element);
  }

  /**
   * Adds the given class (or list of space separated classes) to the Code Mirror DOM element.
   *
   * @param {string} classNames The class name(s) separated by a space.
   * @returns {TextEditor}
   */
  addClass(classNames: string): TextEditor {
    if (this.initialized) {
      this.editorElement.addClass(classNames);
    }
    return this;
  }

  /**
   * Destroy the editor instance and revert back to the original text area.
   * @returns {TextEditor}
   */
  destroy(): TextEditor {
    this.editor.toTextArea();
    this.initialized = false;
    return this;
  }

  /**
   * Give focus to the CodeMirror widget.
   *
   * @returns {TextEditor}
   */
  focus(): TextEditor {
    this.editor.focus();
    return this;
  }

  /**
   * Returns the current value of the text editor
   * @returns {string}
   */
  getValue(): string {
    return this.editor.getDoc().getValue();
  }

  /**
   * Refresh the CodeMirror widget to re-flow any automatically calculated dimensions.
   *
   * @returns {TextEditor}
   */
  refresh(): TextEditor {
    if (this.initialized) {
      this.editor.refresh();
    }
    return this;
  }

  /**
   * Removes the given class (or list of space separated classes) from the Code Mirror DOM element.
   *
   * @param {string} classNames The class name(s).
   * @returns {TextEditor}
   */
  removeClass(classNames: string): TextEditor {
    if (this.initialized) {
      this.editorElement.removeClass(classNames);
    }
    return this;
  }

  /**
   * Call render() to complete the initialization of the TextEditor. All options must be set prior to calling render.
   *
   * @returns {TextEditor} This TextEditor.
   */
  render(): TextEditor {
    if (this.initialized) {
      return this;
    }

    this.editor = CodeMirror.fromTextArea(this.element.domElement, this.options);
    this.editorElement = new Prime.Document.Element(this.editor.getWrapperElement());
    this.initialized = true;
    return this;
  }

  /**
   * Set the height of the editor.
   *
   * @param {number} height The new height of the editor.
   * @returns {TextEditor} This TextEditor.
   */
  setHeight(height: number): TextEditor {
    if (this.initialized) {
      this.editor.setSize(null, height);
    }

    return this;
  }

  /**
   * Set the option on the CodeMirror editor
   *
   * @param {string} name The name of the option.
   * @param {any} value The value of the option.
   * @returns {TextEditor} This TextEditor.
   */
  setOption(name: string, value: any): TextEditor {
    if (this.initialized) {
      this.editor.setOption(name, value);
    }
    return this;
  }

  /**
   * Set the value of the editor.
   *
   * @param {string} value The value to set into the text editor.
   * @returns {TextEditor} This TextEditor.
   */
  setValue(value: string): TextEditor {
    this.editor.getDoc().setValue(value);

    this.editor.save();

    return this;
  }

  /**
   * Save the contents of the text editor back to the original text area. This happens automatically on form submit,
   * but there may be instances you'd like to save this prior to submit. When previewing the content for example.
   *
   * @returns {TextEditor} This TextEditor.
   */
  sync(): TextEditor {
    if (this.initialized) {
      this.editor.save();
    }
    return this;
  }

  /**
   * Set more than one option at a time by providing a map of key value pairs. This is considered an advanced
   * method to set options on the widget. The caller needs to know what properties are valid in the options object.
   *
   * @param {object} options Key value pair of configuration options.
   * @returns {TextEditor} This TextEditor.
   */
  withOptions(options: object): TextEditor {
    if (typeof options === 'undefined' || options === null) {
      return this;
    }

    for (const option in options) {
      if (options.hasOwnProperty(option)) {
        this.options[option] = options[option];
      }
    }
    return this;
  }

  /* ===================================================================================================================
   * Private methods
   * ===================================================================================================================*/
}

// Maintain backward compatibility by assigning to the FusionAuth.UI namespace
FusionAuth.UI.TextEditor = TextEditor;
